import { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cache module (catalogue + défauts par rôle), partagé par tous les composants
let CACHE = null;
let PENDING = null;
const listeners = new Set();

export const loadDroits = (force = false) => {
  if (CACHE && !force) return Promise.resolve(CACHE);
  if (PENDING && !force) return PENDING;
  PENDING = axios.get(`${API}/droits/catalogue`).then(r => {
    CACHE = r.data; PENDING = null;
    listeners.forEach(fn => fn(CACHE));
    return CACHE;
  }).catch(() => { PENDING = null; return CACHE; });
  return PENDING;
};

export const getDroitsCache = () => CACHE;
export const invalidateDroits = () => loadDroits(true);
export const parentOf = (rub) => CACHE?.catalogue?.[rub]?.parent || null;
export const rubriqueOfScreen = (screenId) => CACHE?.screen_to_rubrique?.[screenId] || null;

/** Droit effectif : explicite sur la rubrique → parent → défaut du rôle. `flag` = voir | modifier | vue_directeur | modification_directeur | <fonctionnalité>. */
const PERSO_RUBRIQUES_DEFAUT = ['planning', 'plan_cabinet', 'conges', 'demandes_creneaux', 'recap_mensuel', 'primes', 'taches_jour', 'curriculum_vitae',
  'dossiers_a_traiter', 'infos_patient', 'preconsult', 'patients_adresser', 'indication_refractive', 'programmation',
  'bloc_calendrier', 'bloc_interventions', 'bloc_implants', 'bloc_complications', 'bloc_stats', 'messages', 'coffre_fort', 'redevances'];
const PERSO_FLAGS_TOUT_DEFAUT = ['vue_directeur', 'droits_directeur', 'modification_directeur', 'gestion_complete', 'tous_employes', 'voir_tous', 'modifier_tous', 'valider', 'soldes'];
const isPersoRub = (rub) => {
  const set = CACHE?.perso_rubriques || PERSO_RUBRIQUES_DEFAUT;
  let r = rub;
  while (r) { if (set.includes(r)) return true; r = parentOf(r); }
  return false;
};

/** Bouton « Perso / Tout le monde » : Directeur, ou fonctionnalité accordée sur « À traiter ». */
export const peutBasculerPerimetre = (user) => !!user && (user.role === 'Directeur' || hasDroit(user, 'dossiers_a_traiter', 'bascule_perso_total'));

/** Vue Perso active (et autorisée) : les droits « tout le monde » sont neutralisés dans les rubriques à dimension personnelle. */
export const enModePerso = (user) => !!user && user.vue_perimetre === 'perso' && peutBasculerPerimetre(user);

export const hasDroit = (user, rub, flag = 'voir', opts = {}) => {
  if (!user) return false;
  // opts.brut : droit configuré (matrice Accès), sans neutralisation par la vue « Perso » de l'employé
  if (!opts.brut && flag !== 'bascule_perso_total' && (CACHE?.perso_flags_tout || PERSO_FLAGS_TOUT_DEFAUT).includes(flag) && isPersoRub(rub) && enModePerso(user)) return false;
  if (user.role === 'Directeur') return true;
  const autos = user.autorisations || {};
  let r = rub;
  while (r) {
    const a = autos[r];
    if (a) {
      if (a.droits_directeur) return true;
      if (flag in a) return !!a[flag];
    }
    r = parentOf(r);
  }
  const d = CACHE?.defaults?.[user.role] || {};
  r = rub;
  while (r) {
    const a = d[r];
    if (a && flag in a) return !!a[flag];
    r = parentOf(r);
  }
  // Fonctionnalité non fixée → hérite du flag générique (modifier / vue directeur / actions directeur)
  const fb = CACHE?.feature_fallback?.[`${rub}.${flag}`];
  if (fb && fb !== flag) return hasDroit(user, rub, fb, opts);
  return false;
};

/** Directeur, ou « Tous les droits du directeur » coché sur la rubrique (ou un parent) → même pouvoir que le directeur. */
export const estDirecteur = (user, rub = 'admin') => hasDroit(user, rub, 'droits_directeur');

/** Hook : charge le catalogue et re-rend quand il arrive. Retourne { ready, has(rub, flag), catalogue }. */
export const useDroits = (user) => {
  const [cache, setCache] = useState(CACHE);
  useEffect(() => {
    const fn = (c) => setCache(c);
    listeners.add(fn);
    if (!CACHE) loadDroits();
    return () => listeners.delete(fn);
  }, []);
  return { ready: !!cache, catalogue: cache, has: (rub, flag = 'voir') => hasDroit(user, rub, flag) };
};
